import { useCallback } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { pages } from '@constants/pages';
import { useAuth } from '@contexts/auth';
import { EGender } from '@enums/Gender';
import errorsValidation from '@errors/validation';
import formatterOnlyNumber from '@formatters/onlyNumbers';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import CustomerService from '@services/customers/CustomerService';
import { type IUpdateCustomerInputData } from '@services/customers/dtos/updateCustomer/InputData';
import { type IGetAllOccupationAreasOutputData } from '@services/occupationAreas/dtos/getAllOccupationAreas/OutputData';
import OccupationAreaService from '@services/occupationAreas/OccupationAreaService';
import { useMutation, useQuery } from '@tanstack/react-query';
import countries from 'i18n-iso-countries';
import Locales from 'i18n-iso-countries/langs/pt.json';
import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const validatePhone = (value: string) => {
  return value.replace(/[^0-9]/g, '').length === 11;
};

const genders = [
  { label: 'Masculino', value: 'MASCULINO' },
  { label: 'Feminino', value: 'FEMININO' },
  { label: 'Prefiro não dizer', value: 'none' },
];

const states = [
  { label: 'AC', value: 'AC' },
  { label: 'AL', value: 'AL' },
  { label: 'AP', value: 'AP' },
  { label: 'AM', value: 'AM' },
  { label: 'BA', value: 'BA' },
  { label: 'CE', value: 'CE' },
  { label: 'DF', value: 'DF' },
  { label: 'ES', value: 'ES' },
  { label: 'GO', value: 'GO' },
  { label: 'MA', value: 'MA' },
  { label: 'MT', value: 'MT' },
  { label: 'MS', value: 'MS' },
  { label: 'MG', value: 'MG' },
  { label: 'PA', value: 'PA' },
  { label: 'PB', value: 'PB' },
  { label: 'PR', value: 'PR' },
  { label: 'PE', value: 'PE' },
  { label: 'PI', value: 'PI' },
  { label: 'RJ', value: 'RJ' },
  { label: 'RN', value: 'RN' },
  { label: 'RS', value: 'RS' },
  { label: 'RO', value: 'RO' },
  { label: 'RR', value: 'RR' },
  { label: 'SC', value: 'SC' },
  { label: 'SP', value: 'SP' },
  { label: 'SE', value: 'SE' },
  { label: 'TO', value: 'TO' },
];

const formSchema = z
  .object({
    name: z.string().min(1, {
      message: errorsValidation.required,
    }),
    gender: z.string().min(1, {
      message: errorsValidation.required,
    }),
    email: z.email(errorsValidation.email).min(1, {
      message: errorsValidation.required,
    }),
    phone: z
      .string()
      .refine((val) => validatePhone(val), errorsValidation.phone),
    areaActivity: z.string().min(1, {
      message: errorsValidation.required,
    }),
    yearCompletion: z.string().min(1, {
      message: errorsValidation.required,
    }),
    state: z.string().min(1, {
      message: errorsValidation.required,
    }),
    city: z.string().min(1, {
      message: errorsValidation.required,
    }),
    document: z.string(),
    country: z.string().min(1, {
      message: errorsValidation.required,
    }),
  })
  .superRefine((val, ctx) => {
    if (val.country === 'Brasil' && !val.document) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorsValidation.required,
        path: ['document'],
      });
    }
  });

function generateYears() {
  const years = [];
  for (let i = 1970; i < 2051; i++) {
    years.push({ label: i.toString(), value: i.toString() });
  }
  return years;
}

function Profile() {
  const { onSessionInfo, user } = useAuth();
  countries.registerLocale(Locales);
  const countryNames = countries.getNames('pt', { select: 'official' });

  const { data: occupationAreas } = useQuery<IGetAllOccupationAreasOutputData>({
    queryFn: async () => {
      const response = await OccupationAreaService.getAll();
      return response;
    },
    queryKey: ['occupationArea'],
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      name: user?.name || '',
      gender: user?.gender || 'none',
      email: user?.email || '',
      phone: user?.phone || '',
      areaActivity: occupationAreas?.list.find(
        (item) => item.name === user?.occupationArea,
      )
        ? occupationAreas.list
            .find((item) => item.name === user?.occupationArea)
            ?.id.toString()
        : '',
      yearCompletion: user?.yearGraduated || '',
      state: user?.occupationState || '',
      city: user?.occupationCity || '',
      document: user?.document || '',
      country: user?.country || '',
    },
  });

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    IUpdateCustomerInputData
  >({
    mutationFn: CustomerService.update,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível atualizar o perfil. Tente novamente',
      });
    },
    onSuccess: async () => {
      toast.success('Sucesso!', {
        description: 'Perfil atualizado com sucesso',
      });
      await onSessionInfo();
    },
  });

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        email: values.email,
        gender: values.gender === 'none' ? null : (values.gender as EGender),
        id: user!.id,
        name: values.name,
        occupationArea: Number(values.areaActivity),
        occupationCity: values.city,
        occupationState: values.state,
        phone: formatterOnlyNumber(values.phone),
        picture: user!.picture,
        yearGraduated: values.yearCompletion,
        document: formatterOnlyNumber(values.document),
        country: values.country,
      });
    },
    [mutateAsync, user],
  );

  return (
    <div>
      <PageTitle hasBackButton title="Perfil" />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">Nome</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">CPF</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <PatternFormat
                      autoComplete="off"
                      customInput={Input}
                      format="###.###.###-##"
                      getInputRef={ref}
                      mask="_"
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">Sexo</FormLabel>
                    <FormMessage />
                  </div>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {genders.map((gender) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={gender.value}
                          value={gender.value}>
                          {gender.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">E-mail</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">Celular</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <PatternFormat
                      autoComplete="off"
                      customInput={Input}
                      format="(##) #####-####"
                      getInputRef={ref}
                      mask="_"
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areaActivity"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">
                      Área de atuação
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {occupationAreas?.list.map((area) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={area.id}
                          value={area.id.toString()}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="yearCompletion"
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">
                      Ano de formação
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Popover>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? generateYears().find(
                                (option) => option.value === field.value,
                              )?.label
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {generateYears().map((option) => (
                              <CommandItem
                                className="data-[selected=true]:bg-tertiary"
                                key={option.value}
                                onSelect={() => {
                                  form.setValue(
                                    `yearCompletion`,
                                    option.value,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                value={option.value}>
                                <PopoverClose className="flex w-full flex-row items-center">
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      option.value === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {option.label}
                                </PopoverClose>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">
                      Estado em que atua
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Popover>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? states.find(
                                (option) => option.value === field.value,
                              )?.label
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {states.map((option) => (
                              <CommandItem
                                className="data-[selected=true]:bg-tertiary"
                                key={option.value}
                                onSelect={() => {
                                  form.setValue(`state`, option.value, {
                                    shouldValidate: true,
                                  });
                                }}
                                value={option.value}>
                                <PopoverClose className="flex w-full flex-row items-center">
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      option.value === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {option.label}
                                </PopoverClose>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">
                      Cidade em que atua
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-tertiary">
                      País que reside
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Popover>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? Object.values(countryNames)
                                .map((country) => ({
                                  label: country,
                                  value: country,
                                }))
                                .find((option) => option.value === field.value)
                                ?.label
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {Object.values(countryNames)
                              .map((country) => ({
                                label: country,
                                value: country,
                              }))
                              .map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.value}
                                  onSelect={() => {
                                    form.setValue(`country`, option.value, {
                                      shouldValidate: true,
                                    });
                                  }}
                                  value={option.value}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        option.value === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.label}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
          </div>
          <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-[repeat(2,_15rem)] sm:justify-end">
            <Button
              asChild
              disabled={isPending}
              type="button"
              variant="destructive">
              <Link replace to={pages.home}>
                Cancelar
              </Link>
            </Button>
            <Button disabled={isPending} type="submit" variant="secondary">
              {isPending ? (
                <LoaderCircle className="text-background animate-spin" />
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
          <p className="mt-8 text-center text-xs font-light">
            Em caso de cancelamento da assinatura, entre em contato pelo nosso
            whatsapp.
          </p>
        </form>
      </Form>
    </div>
  );
}

export default Profile;
